import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  buildCutSequence,
  buildFullStripGuides,
  buildStripRipGuides,
  getExportBoardClassName,
  getSheetSize,
} from "@plaatplan/core";
import {
  resolveCutSequenceSummary,
  resolveOffcutLabel,
  resolvePlacementCaption,
  resolveStripModeBanner,
} from "@plaatplan/i18n";
import { useLocale } from "../i18n/useLocale";

const SCREEN_BOARD_MAX_HEIGHT = 520;
const PRINT_LANDSCAPE_MAX_WIDTH_MM = 277;
const PRINT_LANDSCAPE_MAX_HEIGHT_MM = 190;

function mmToPx(mm) {
  return mm * (96 / 25.4);
}

function getScreenStageWidth(stageElement, wrapElement) {
  const measured = stageElement?.clientWidth || wrapElement?.clientWidth;
  if (measured) {
    return measured;
  }

  if (typeof window !== "undefined") {
    return Math.max(280, window.innerWidth - 48);
  }

  return null;
}

function computeBoardScale(
  stageWidth,
  boardLength,
  boardWidth,
  forPrint = false,
  printLandscape = false,
  printLandscapeMaxHeightMm = PRINT_LANDSCAPE_MAX_HEIGHT_MM,
) {
  if (forPrint && printLandscape) {
    const maxWidth = mmToPx(PRINT_LANDSCAPE_MAX_WIDTH_MM);
    const maxHeight = mmToPx(printLandscapeMaxHeightMm);
    return Math.min(maxWidth / boardLength, maxHeight / boardWidth);
  }

  if (!stageWidth) {
    return Math.min(SCREEN_BOARD_MAX_HEIGHT / boardWidth, 0.35);
  }

  return Math.min(stageWidth / boardLength, SCREEN_BOARD_MAX_HEIGHT / boardWidth);
}

function renderCutGuide(cut, {
  scale,
  length,
  width,
  strategy,
  extraClassName = "",
}) {
  const segmentLength = cut.end - cut.start;
  const labelOffset = (cut.labelPos ?? cut.start) - cut.start;
  const isStripCross = strategy === "strips" && cut.kind === "strip-cross";
  const isStripRip = strategy === "strips" && cut.kind === "strip-rip";
  const isFullRip = isStripRip
    || (cut.axis === "horizontal" && segmentLength >= length - 1)
    || (cut.axis === "vertical" && segmentLength >= width - 1);

  return (
    <div
      key={`${cut.axis}-${cut.position}-${cut.number}`}
      className={[
        "export-cut-guide",
        cut.axis,
        isStripCross ? "export-cut-guide--strip-cross" : "",
        isStripRip ? "export-cut-guide--strip-rip" : "",
        !isStripCross && !isStripRip
          ? (isFullRip ? "export-cut-guide--full" : "export-cut-guide--partial")
          : "",
        extraClassName,
      ].filter(Boolean).join(" ")}
      style={cut.axis === "vertical"
        ? {
          left: `${cut.position * scale}px`,
          top: `${cut.start * scale}px`,
          height: `${segmentLength * scale}px`,
        }
        : {
          top: `${cut.position * scale}px`,
          left: `${cut.start * scale}px`,
          width: `${segmentLength * scale}px`,
        }}
    >
      <span
        className="export-cut-number"
        style={cut.axis === "vertical"
          ? { top: `${labelOffset * scale}px` }
          : { left: `${labelOffset * scale}px` }}
      >
        {cut.number}
      </span>
    </div>
  );
}

export function ExportBoard({
  sourcePanel,
  sheet,
  offcuts = [],
  showOffcuts = false,
  showCutSequence = false,
  cutSimulationStep = null,
  simulationActiveStock = null,
  strategy = "efficient_cutting",
  stripsNoCrosscut = false,
  minOffcutLength = 500,
  minOffcutWidth = 300,
  printLandscape = false,
}) {
  const { display } = useLocale();
  const stageRef = useRef(null);
  const wrapRef = useRef(null);
  const { length, width } = getSheetSize(sheet, sourcePanel);
  const [scale, setScale] = useState(() => computeBoardScale(
    typeof window !== "undefined" ? Math.max(280, window.innerWidth - 48) : null,
    length,
    width,
  ));
  const [isPrintLayout, setIsPrintLayout] = useState(false);

  useEffect(() => {
    const updateScale = (forPrint = false) => {
      const stageWidth = forPrint && printLandscape
        ? mmToPx(PRINT_LANDSCAPE_MAX_WIDTH_MM)
        : getScreenStageWidth(stageRef.current, wrapRef.current);
      if (!forPrint && !stageWidth) {
        return;
      }
      setScale(computeBoardScale(
        stageWidth,
        length,
        width,
        forPrint,
        printLandscape,
        PRINT_LANDSCAPE_MAX_HEIGHT_MM,
      ));
    };

    const onBeforePrint = () => {
      flushSync(() => {
        setIsPrintLayout(true);
        updateScale(true);
      });
    };

    const onAfterPrint = () => {
      flushSync(() => {
        setIsPrintLayout(false);
      });
      updateScale(false);
    };

    updateScale(false);
    const onResize = () => updateScale(false);
    const observer = new ResizeObserver(onResize);
    const observeTarget = wrapRef.current || stageRef.current;
    if (observeTarget) observer.observe(observeTarget);
    window.addEventListener("resize", onResize);
    window.addEventListener("beforeprint", onBeforePrint);
    window.addEventListener("afterprint", onAfterPrint);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("beforeprint", onBeforePrint);
      window.removeEventListener("afterprint", onAfterPrint);
    };
  }, [length, width, printLandscape]);

  const simulationMode = cutSimulationStep != null;

  const cutSequence = showCutSequence || simulationMode
    ? buildCutSequence(sheet, sourcePanel, {
      strategy,
      stripsNoCrosscut,
      minOffcutLength,
      minOffcutWidth,
    })
    : [];

  const cutSummary = !simulationMode
    && resolveCutSequenceSummary(display, cutSequence, strategy);

  const stripGuides = !simulationMode && strategy === "strips"
    ? (stripsNoCrosscut
      ? buildFullStripGuides(sheet, sourcePanel).slice(0, 12)
      : buildStripRipGuides(cutSequence, length, width))
    : [];

  const boardClassName = getExportBoardClassName(strategy, stripsNoCrosscut, offcuts.length > 0);
  const stripBanner = !simulationMode && resolveStripModeBanner(display, strategy, stripsNoCrosscut);
  const offcutLabel = resolveOffcutLabel(display, strategy, stripsNoCrosscut);
  const useLandscapePrint = isPrintLayout && printLandscape;
  const boardStyle = {
    width: `${length * scale}px`,
    height: `${width * scale}px`,
  };
  const printStageStyle = useLandscapePrint
    ? boardStyle
    : undefined;

  const boardContent = (
    <div className={boardClassName} style={boardStyle}>
          {strategy === "strips" && stripGuides.map((strip, index) => (
            <div
              key={`strip-${index}-${strip.x}-${strip.y}`}
              className={`export-strip-band${stripsNoCrosscut ? " no-crosscut" : ""}`}
              style={{
                left: `${strip.x * scale}px`,
                top: `${strip.y * scale}px`,
                width: `${strip.length * scale}px`,
                height: `${strip.width * scale}px`,
              }}
            >
              <span>{strip.label}</span>
            </div>
          ))}

          {simulationMode && simulationActiveStock && (
            <div
              className="export-sim-active-region"
              style={{
                left: `${simulationActiveStock.x * scale}px`,
                top: `${simulationActiveStock.y * scale}px`,
                width: `${simulationActiveStock.length * scale}px`,
                height: `${simulationActiveStock.width * scale}px`,
              }}
              aria-hidden="true"
            />
          )}

          {showOffcuts && offcuts.map((offcut, index) => (
            <div
              key={`${offcut.label}-${index}`}
              className={`export-offcut${stripsNoCrosscut && strategy === "strips" ? " future-offcut" : ""}`}
              style={{
                left: `${offcut.x * scale}px`,
                top: `${offcut.y * scale}px`,
                width: `${offcut.length * scale}px`,
                height: `${offcut.width * scale}px`,
              }}
            >
              <strong>{offcutLabel}</strong>
              <span>{display.formatPanelSize(offcut.length, offcut.width)}</span>
            </div>
          ))}

          {sheet.placements.map((placement) => (
            <div
              key={`${placement.panelId}-${placement.occurrence}`}
              className={`export-placed-panel${stripsNoCrosscut && strategy === "strips" ? " strip-cut-part" : ""}`}
              style={{
                left: `${placement.x * scale}px`,
                top: `${placement.y * scale}px`,
                width: `${placement.length * scale}px`,
                height: `${placement.width * scale}px`,
              }}
            >
              <strong>{display.formatPanelSize(placement.originalLength, placement.originalWidth)}</strong>
              <span>{resolvePlacementCaption(display, strategy, stripsNoCrosscut, placement.name)}</span>
            </div>
          ))}

          {simulationMode && cutSequence
            .filter((cut) => cut.number <= cutSimulationStep)
            .map((cut) => renderCutGuide(cut, {
              scale,
              length,
              width,
              strategy,
              extraClassName: [
                cutSimulationStep > 0 && cut.number === cutSimulationStep ? "export-cut-guide--current" : "",
                cut.number < cutSimulationStep ? "export-cut-guide--past" : "",
              ].filter(Boolean).join(" "),
            }))}

          {!simulationMode && showCutSequence && cutSequence.map((cut) => renderCutGuide(cut, {
            scale,
            length,
            width,
            strategy,
          }))}
    </div>
  );

  return (
    <div ref={wrapRef} className="export-board-wrap">
      <div className="export-board-meta">
        <div className="export-board-meta-info">
          <strong>{display.t("web.exportBoard.sheetTitle", { number: sheet.index + 1 })}</strong>
          <span>
            {display.formatThickness(sheet.thickness)}
            {" · "}
            {display.formatSheet(length, width)}
          </span>
          {cutSummary && <span className="export-cut-summary">{cutSummary}</span>}
        </div>
      </div>
      {stripBanner && (
        <div className={`strip-mode-banner ${stripsNoCrosscut ? "no-crosscut" : "crosscut"}`}>
          {stripBanner}
        </div>
      )}
      <div
        ref={stageRef}
        className={`export-board-stage${useLandscapePrint ? " export-board-stage--print-landscape" : ""}`}
        style={printStageStyle}
      >
        {boardContent}
      </div>
    </div>
  );
}
